import type { ChangesAgencyFilterCatalog } from '../types/changesAgencyFilter';

/** Linha mínima de `/sources/summary` (evita import circular com `api.ts`). */
export type SourceSummaryRow = {
  name?: string | null;
  uf?: string | null;
  municipality?: string | null;
};

/** Mesmo mapa de UF → nome usado no Dashboard (Fontes ativas). */
const UF_TO_STATE_PT: Record<string, string> = {
  ac: 'Acre',
  al: 'Alagoas',
  ap: 'Amapá',
  am: 'Amazonas',
  ba: 'Bahia',
  ce: 'Ceará',
  df: 'Distrito Federal',
  es: 'Espírito Santo',
  go: 'Goiás',
  ma: 'Maranhão',
  mt: 'Mato Grosso',
  ms: 'Mato Grosso do Sul',
  mg: 'Minas Gerais',
  pa: 'Pará',
  pb: 'Paraíba',
  pr: 'Paraná',
  pe: 'Pernambuco',
  pi: 'Piauí',
  rj: 'Rio de Janeiro',
  rn: 'Rio Grande do Norte',
  rs: 'Rio Grande do Sul',
  ro: 'Rondônia',
  rr: 'Roraima',
  sc: 'Santa Catarina',
  sp: 'São Paulo',
  se: 'Sergipe',
  to: 'Tocantins',
};

function stateHeadingForUf(ufKey: string): string {
  if (!ufKey || ufKey === '_') return 'Outros';
  return UF_TO_STATE_PT[ufKey] ?? ufKey.toUpperCase();
}

function sortUfKeys(ufKeys: string[]): string[] {
  return [...ufKeys].sort((a, b) => {
    const la = stateHeadingForUf(a).localeCompare(stateHeadingForUf(b), 'pt-BR');
    if (la !== 0) return la;
    return a.localeCompare(b);
  });
}

/**
 * Catálogo do filtro «Agência» alinhado ao card Fontes ativas:
 * mesmos endpoints `/sources/summary?scope=` (federal | state | municipal).
 */
export function buildAgencyFilterCatalogFromSourceSummaries(
  federal: SourceSummaryRow[],
  state: SourceSummaryRow[],
  municipal: SourceSummaryRow[],
): ChangesAgencyFilterCatalog {
  const fedNames = federal
    .map((s) => (s.name || '').trim())
    .filter(Boolean);
  const federacao = Array.from(new Set(fedNames)).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  const byUf: Record<string, Set<string>> = {};
  for (const s of state) {
    const n = (s.name || '').trim();
    if (!n) continue;
    const uf = (s.uf || '').trim().toLowerCase() || '_';
    if (!byUf[uf]) byUf[uf] = new Set();
    byUf[uf].add(n);
  }
  const ufKeys = sortUfKeys(Object.keys(byUf));
  const estados = ufKeys.map((uf) => ({
    state: stateHeadingForUf(uf),
    names: Array.from(byUf[uf] ?? []).sort((a, b) => a.localeCompare(b, 'pt-BR')),
  }));

  const byStateThenCity: Record<string, Record<string, Set<string>>> = {};
  for (const s of municipal) {
    const n = (s.name || '').trim();
    if (!n) continue;
    const uf = (s.uf || '').trim().toLowerCase() || '_';
    const city = (s.municipality || '').trim() || '—';
    if (!byStateThenCity[uf]) byStateThenCity[uf] = {};
    if (!byStateThenCity[uf][city]) byStateThenCity[uf][city] = new Set();
    byStateThenCity[uf][city].add(n);
  }

  const munUfKeys = sortUfKeys(Object.keys(byStateThenCity));
  const municipios = munUfKeys.map((ufKey) => {
    const citiesMap = byStateThenCity[ufKey] ?? {};
    const cityKeys = Object.keys(citiesMap).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    return {
      state: stateHeadingForUf(ufKey),
      cities: cityKeys.map((city) => ({
        city,
        names: Array.from(citiesMap[city] ?? []).sort((a, b) => a.localeCompare(b, 'pt-BR')),
      })),
    };
  });

  return { federacao, estados, municipios };
}
